import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowRightLeft, CalendarPlus, IndentDecrease, IndentIncrease, Repeat as RepeatIcon } from 'lucide-react'
import { Banner, Button, Checkbox, Field, Row, Tag, type SwipeAction, useToast } from '../ui'
import {
  tickTask, useAddItem, useClearDone, useCompleteTask, useMoveLine, useRemoveItem, useReorderItems, useSetItemDone,
  useSetItemFields, useSetItemParent, useUpdateItem,
} from '../lib/mutations'
import { useWriteError } from '../lib/useAction'
import { canClear, dueLabel, foldTasks, isCleared, pinnedByDue, shortDay } from '../lib/taskRules'
import { useLinger } from '../lib/useLinger'
import { repeatLabel } from '../lib/repeatParse'
import { dropped, droppedPhrase, movePatch } from '../lib/moveLine'
import { addTask } from '../lib/listAdd'
import { berlinDay, dayStart } from '../lib/today'
import { useBlurField } from '../lib/useBlurField'
import { moveTo as reorderTo } from '../lib/reorder'
import { usePointerReorder } from '../lib/usePointerReorder'
import { listSummary } from '../lib/listSummary'
import { restoreItem } from '../lib/db'
import { useQueryClient } from '@tanstack/react-query'
import { useFeed } from '../lib/queries'
import { heroIsVenue, heroUrl, posterUrl } from '../lib/entries'
import { leadingOf, metaLine, plateFor, rowTitle } from '../lib/rowMeta'
import { EventSheet } from './EventSheet'
import { TaskSheet, type TaskPatch } from './TaskSheet'
import type { FeedRow, List, ListItem } from '../lib/types'
import { RowMore } from './RowMore'
import './EventRow.css'

/** A day boundary the render already crossed and hasn't re-grouped for yet
 *  ticks on the next check rather than waiting for the interval below, which
 *  is what keeps "Done" from holding yesterday's rows for up to a minute
 *  after midnight on a screen that has been open all evening. */
function useToday(): string {
  const [today, setToday] = useState(() => berlinDay(new Date()))
  useEffect(() => {
    const id = setInterval(() => {
      const now = berlinDay(new Date())
      setToday(prev => (prev === now ? prev : now))
    }, 60_000)
    return () => clearInterval(id)
  }, [])
  return today
}

export function TaskList({ list, items }: { list: List; items: ListItem[] }) {
  const toast = useToast()
  const qc = useQueryClient()
  const today = useToday()
  const [draft, setDraft] = useState('')
  const [edit, setEdit] = useState<string | null>(null)
  const [sheetFor, setSheetFor] = useState<ListItem | null>(null)
  const [showDone, setShowDone] = useState(false)
  // D1: ids ticked within LINGER_MS — done in the data (the optimistic patch
  // wrote `done_at` on the tap) and still drawn open, in place, struck
  // through. Then `leaving` for the Row's collapse, then gone. Two sets of
  // ids in component state (`useLinger`, shared with the other kinds),
  // never a second column: the fold's count reads `done_at` and is right
  // from the tap.
  const linger = useLinger()
  const { lingering, leaving } = linger
  // L8: which event's sheet is open, from tapping an item that carries an
  // `entry_id` — the item editor is not reached from that tap.
  const [openEntry, setOpenEntry] = useState<FeedRow | null>(null)

  const addM = useAddItem()
  const toggleM = useSetItemDone()
  const completeM = useCompleteTask()
  const clearM = useClearDone()
  const moveM = useMoveLine()
  const removeM = useRemoveItem()
  const updateM = useUpdateItem()
  const fieldsM = useSetItemFields()
  const parentM = useSetItemParent()
  const reorderM = useReorderItems()
  const error = useWriteError(addM, toggleM, completeM, clearM, moveM, removeM, updateM, fieldsM, parentM, reorderM)
  const busy = addM.isPending

  const feedQ = useFeed()
  const feedById = useMemo(() => new Map((feedQ.data ?? []).map(e => [e.id, e])), [feedQ.data])
  const entryFor = (i: ListItem): FeedRow | null => (i.entry_id ? feedById.get(i.entry_id) ?? null : null)

  const open = items.filter(i => !i.done_at)

  async function add(text: string) {
    if (!text.trim()) return
    const out = await addTask({ addM }, list, items, text)
    if (out.ok && !out.empty) setDraft('')
  }

  /** D1/D2/D6: the tick. The write is `tickTask`'s choice (a repeating task
   *  goes through `complete_task`); the row lingers, then leaves. Undo is
   *  untick. An untick during the linger cancels it and the row stays. */
  function toggle(i: ListItem) {
    if (i.done_at) {
      linger.cancel(i.id)
      tickTask({ done: toggleM, complete: completeM }, i, today)
      return
    }
    const { nextDueOn } = tickTask({ done: toggleM, complete: completeM }, i, today)
    linger.start(i.id)
    const repeating = !!nextDueOn
    toast({
      text: repeating ? `Done "${i.body}" · next ${shortDay(nextDueOn as string)}` : `Done "${i.body}"`,
      action: {
        label: 'Undo',
        onClick: () => {
          linger.cancel(i.id)
          toggleM.mutate({ id: i.id, done: false })
          if (repeating) toast({ text: 'Unticked · the next one stays' })
        },
      },
    })
  }
  // A tick the server refused: the optimistic row is back to open (the
  // banner says why), so the linger must not go on to collapse it.
  useEffect(() => {
    if (toggleM.isError || completeM.isError) linger.cancelAll()
  }, [toggleM.isError, completeM.isError, linger])

  /** D3: Clear hides every done row older than today; Undo puts the cutoff back. */
  function clearDone(n: number) {
    const was = list.done_cleared_at ?? null
    clearM.mutate({ listId: list.id, at: dayStart(today).toISOString() })
    toast({ text: `Cleared · ${n}`, action: { label: 'Undo', onClick: () => clearM.mutate({ listId: list.id, at: was }) } })
  }

  /** D8: a line to another list. Undo (a move back) only when nothing was
   *  dropped on the way — a move back cannot restore a date it lost. */
  function moveTo(i: ListItem, to: List) {
    const lost = dropped(list.kind, to.kind, i)
    moveM.mutate({ id: i.id, toListId: to.id, patch: movePatch(list.kind, to.kind, to.id) })
    setSheetFor(null)
    const phrase = droppedPhrase(lost)
    toast(phrase
      ? { text: `Moved to ${to.name} · ${phrase}` }
      : { text: `Moved to ${to.name}`, action: { label: 'Undo', onClick: () => moveM.mutate({ id: i.id, toListId: list.id, patch: movePatch(to.kind, list.kind, list.id) }) } })
  }

  /** The sheet's Save: the title through `updateItem`, the rest in one
   *  `setItemFields` write; nothing changed is no write. */
  function saveSheet(i: ListItem, patch: TaskPatch) {
    const { body, ...fields } = patch
    if (body) updateM.mutate({ id: i.id, patch: { body } })
    if (Object.keys(fields).length) fieldsM.mutate({ id: i.id, patch: fields })
    setSheetFor(null)
  }

  /** An unchanged or emptied title is no write — emptied puts the old one
   *  back, since a task with no words is not a task. */
  function save(i: ListItem, body: string) {
    setEdit(null)
    const next = body.trim()
    if (next && next !== i.body) updateM.mutate({ id: i.id, patch: { body: next } })
  }

  // F7: removal is a swipe and a toast, never a confirmation dialog.
  async function restore(i: ListItem, gone: Promise<unknown>) {
    const { error: err } = await restoreItem(i, gone)
    if (!err) void qc.invalidateQueries({ queryKey: ['list_items'] })
  }

  const del = (i: ListItem) => {
    // The delete's own promise, so Undo writes the row back after it and
    // not before (a2-14): the row returns under its own id now, which a
    // delete still in flight would otherwise remove a second time.
    const gone = removeM.mutateAsync(i.id).catch(() => {})
    toast({ text: `Removed "${i.body}"`, action: { label: 'Undo', onClick: () => void restore(i, gone) } })
  }

  /** Rule: swipe right indents under the previous top-level row; swipe right
   *  again outdents. The first top-level row has nothing above it to join
   *  and stays put. */
  function indent(i: ListItem) {
    if (i.parent_id) { parentM.mutate({ id: i.id, parent_id: null }); return }
    const top = open.filter(o => !o.parent_id).sort((a, b) => a.position - b.position)
    const idx = top.findIndex(t => t.id === i.id)
    if (idx <= 0) return
    parentM.mutate({ id: i.id, parent_id: top[idx - 1].id })
  }


  // The one fold for here and the desk (`lib/taskRules.ts`): open rows in
  // due-then-position order, each with its subtasks, done rows by day under
  // "Done" (D3, D4). A row in `lingering` is still drawn open (D1). `today`
  // ticks over at local midnight (`useToday`) for the group labels.
  const clearedAt = list.done_cleared_at ?? null
  const fold = foldTasks(items, { today, clearedAt, dayOf: berlinDay, lingering })
  const foldedCount = fold.doneCount
  const top = fold.top.map(t => t.item)
  const kids = new Map(fold.top.map(t => [t.item.id, t.children]))
  const childrenOf = (id: string) => kids.get(id) ?? []
  const doneRows = items.filter(i => !!i.done_at)
  const clearable = canClear(doneRows, clearedAt, today, berlinDay)
  // What Clear would hide: done before today and not already cleared.
  const clearCount = doneRows.filter(i => !isCleared(i, clearedAt) && berlinDay(i.done_at as string) < today).length

  /** Reorder among siblings only — dragging a top-level row past another
   *  top-level row, or a sub-task past its own parent's other sub-tasks.
   *  `group` is `parent_id` (or `'top'`), and the hook offers no drop target
   *  across groups, so a drag never silently re-parents a task. Long-press
   *  then drag with a finger, plain drag with a mouse (UI-audit a2-11: the
   *  HTML5 `draggable` this replaced never starts from a touch). */
  const drag = usePointerReorder({
    onDrop: (dragId, overId, group) => {
      // D4: a row the due-sort pinned is in group 'due' — never a drop
      // target for a manual row, and a drop among pinned rows is refused
      // rather than written as a position the sort would ignore.
      if (group === 'due') return
      const siblings = group === 'top' ? top.filter(t => !pinnedByDue(t, today)) : childrenOf(group)
      const order = reorderTo(siblings.map(s => s.id), dragId, overId)
      if (order) reorderM.mutate(order)
    },
  })
  const draggableProps = (item: ListItem, group: string) => {
    const g = group === 'top' && pinnedByDue(item, today) ? 'due' : group
    return { className: drag.classFor(item.id), ...drag.bind(item.id, g) }
  }

  return (
    <>
      <div className="tasklist__head">
        <span className="tasklist__count">{listSummary(list, items, [], new Date()).meta}</span>
      </div>

      {error && <Banner tone="bad">{error}</Banner>}

      <div className="lists-addrow">
        <Field label="Add" className="lists-addrow__field">
          <Field.Input
            value={draft} placeholder="Add…" autoComplete="off"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void add(draft) }}
          />
        </Field>
        <Button variant="primary" disabled={busy || !draft.trim()} onClick={() => void add(draft)}>Add</Button>
      </div>

      {!top.length && !foldedCount ? (
        <div className="stub">All clear.</div>
      ) : (
        <div className="lists-group">
          {top.map(t => (
            <div key={t.id} {...draggableProps(t, 'top')}>
              <TaskRow
                item={t} today={today} entry={entryFor(t)} onOpenEvent={setOpenEntry} leaving={leaving.has(t.id)}
                onToggle={() => toggle(t)} onDelete={() => del(t)} onIndent={() => indent(t)}
                editing={edit === t.id} onEdit={() => setEdit(t.id)}
                onSave={body => save(t, body)} onCancel={() => setEdit(null)}
                onOpenSheet={() => setSheetFor(t)}
              />
              {childrenOf(t.id).map(c => (
                <div key={c.id} {...draggableProps(c, t.id)}>
                  <TaskRow
                    item={c} today={today} sub entry={entryFor(c)} onOpenEvent={setOpenEntry} leaving={leaving.has(c.id)}
                    onToggle={() => toggle(c)} onDelete={() => del(c)} onIndent={() => indent(c)}
                    editing={edit === c.id} onEdit={() => setEdit(c.id)}
                    onSave={body => save(c, body)} onCancel={() => setEdit(null)}
                    onOpenSheet={() => setSheetFor(c)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {foldedCount > 0 && (
        <>
          <div className="tasklist__donebar">
            <button type="button" className="lists-grouphead tasklist__donehead" aria-expanded={showDone} onClick={() => setShowDone(v => !v)}>
              <span>Done · {foldedCount}</span>
            </button>
            {/* D3: Clear hides what is older than today; it deletes nothing,
                so it is a text button and not a destructive one. */}
            {clearable && (
              <button type="button" className="tasklist__clear" onClick={() => clearDone(clearCount)}>
                Clear
              </button>
            )}
          </div>
          {showDone && fold.done.map(g => (
            <div key={g.key} className="tasklist__donegroup">
              <p className="lists-grouphead tasklist__dayhead"><span>{g.label}</span></p>
              <div className="lists-group">
                {g.lines.map(i => (
                  <TaskRow
                    key={i.id} item={i} today={today} entry={entryFor(i)} onOpenEvent={setOpenEntry}
                    onToggle={() => toggle(i)} onDelete={() => del(i)}
                    editing={false} onSave={() => {}} onCancel={() => {}} onOpenSheet={() => {}}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {sheetFor && (
        <TaskSheet
          item={sheetFor} list={list}
          onClose={() => setSheetFor(null)}
          onSave={patch => saveSheet(sheetFor, patch)}
          onMove={to => moveTo(sheetFor, to)}
        />
      )}

      <EventSheet e={openEntry} onClose={() => setOpenEntry(null)} />
    </>
  )
}

function TaskRow({ item, today, sub, entry, onOpenEvent, leaving, onToggle, onDelete, onIndent, editing, onEdit, onSave, onCancel, onOpenSheet }: {
  item: ListItem; today: string; sub?: boolean
  /** D1: ticked, lingered, and now collapsing out of the open list. */
  leaving?: boolean
  /** L8: the feed row this item was added from, or null once it has left the
   *  feed — `entryFor` above tells the two apart from "never linked at all". */
  entry?: FeedRow | null
  onOpenEvent?: (e: FeedRow) => void
  /** `onIndent` and `onEdit` are absent on a row folded into Done, which
   *  is only ever unticked or removed. */
  onToggle: () => void; onDelete: () => void; onIndent?: () => void
  editing: boolean; onEdit?: () => void; onSave: (body: string) => void; onCancel: () => void
  /** T1.2: the task sheet — note, due, repeat, move. The chip and the "…"
   *  menu's Due date / Repeat / Move to all open it; the title still edits
   *  inline. */
  onOpenSheet: () => void
}) {
  const [draftBody, setDraftBody] = useState(item.body)
  // Escape puts the title back and leaves edit mode without a write; Enter
  // and blur save (UI-audit a2-10).
  const keys = useBlurField(() => { setDraftBody(item.body); onCancel() })
  const due = item.due_on ? dueLabel(item.due_on, today) : null
  const every = item.repeat_every ?? null, unit = item.repeat_unit ?? null
  const repeats = every && unit ? repeatLabel({ every, unit }) : null
  const note = item.note ?? null

  const checkbox = (
    <Checkbox checked={!!item.done_at} onChange={onToggle}
      aria-label={item.done_at ? `Mark "${item.body}" not done` : `Mark "${item.body}" done`} />
  )
  const indentLabel = item.parent_id ? 'Outdent' : 'Indent'
  const swipe: { left: SwipeAction; right?: SwipeAction } = {
    left: { label: 'Remove', onAction: onDelete, leaves: true },
    ...(onIndent ? { right: { label: indentLabel, onAction: onIndent } } : {}),
  }
  // a2-32: the swipe's two actions, and Edit, behind a "…" you can see.
  // An event item has no Edit — its tap opens the event, not a title field.
  // A row folded into Done (no `onIndent`) is only unticked or removed; an
  // open task's menu reaches everything the sheet holds (D5–D8).
  const more = (canEdit: boolean) => (
    <RowMore
      name={item.body} onEdit={canEdit ? onEdit : undefined} onRemove={onDelete}
      extra={onIndent ? [
        ...(canEdit ? [
          { label: 'Due date…', icon: <CalendarPlus size={20} aria-hidden="true" />, onSelect: onOpenSheet },
          { label: 'Repeat…', icon: <RepeatIcon size={20} aria-hidden="true" />, onSelect: onOpenSheet },
        ] : []),
        { label: 'Move to…', icon: <ArrowRightLeft size={20} aria-hidden="true" />, onSelect: onOpenSheet },
        {
          label: indentLabel,
          icon: item.parent_id ? <IndentDecrease size={20} aria-hidden="true" /> : <IndentIncrease size={20} aria-hidden="true" />,
          onSelect: onIndent,
        },
      ] : []}
    />
  )

  // L8: an item added from an event's sheet renders with the event's own
  // anatomy — its picture, title and when/where, from `EventItemRow` — with
  // the checkbox moved to the trailing slot rather than leading. Once the
  // event has left the feed (`entry` null but `entry_id` still set) it falls
  // back to plain text with a `Passed` tag: `body` is the row's own title,
  // stored at add time, which is what makes that fallback possible.
  if (item.entry_id) {
    if (entry) {
      return (
        <EventItemRow entry={entry} trailing={<>{checkbox}{more(false)}</>} swipe={swipe} leaving={leaving} onOpen={() => onOpenEvent?.(entry)} />
      )
    }
    return (
      <Row
        className={`u-row--list${sub ? ' lists-item--sub' : ''}`}
        leaving={leaving}
        leading={checkbox}
        title={<span className="lists-itembody lists-itembody--wrap">{item.body}</span>}
        meta={<Tag>Passed</Tag>}
        trailing={more(false)}
        swipe={swipe}
      />
    )
  }

  const title = editing ? (
    <Field.Input
      autoFocus value={draftBody} className="tasklist__editfield"
      onChange={e => setDraftBody(e.target.value)}
      onBlur={keys.onBlurOf(() => onSave(draftBody))}
      onKeyDown={keys.onKeyDown}
    />
  ) : (
    <button type="button" className={`lists-itembody lists-itembody--wrap${item.done_at ? ' is-done' : ''}`} onClick={onEdit}>
      {item.body}
    </button>
  )

  // The due chip is the row's trailing control rather than its meta line
  // (UI-audit a2-08). Under the title it was a 92×20 target: a 52px row has
  // no room for two stacked 44px targets, and the meta line's own
  // `overflow: hidden` clipped any padding that tried. The trailing slot is
  // the full height of the row and outside the swipe, which is what `Row`
  // keeps it for — a control, not a line of text. D5: with no date there is
  // nothing in the slot but the "…" — the calendar glyph that stood in for
  // the chip went with the plan, the date is set from the menu or by typing.
  // D7: the note is the meta line, one dim truncated line, only when set.
  return (
    <Row
      className={`u-row--list${sub ? ' lists-item--sub' : ''}${note ? ' tasklist__row--noted' : ''}`}
      leaving={leaving}
      leading={checkbox}
      title={title}
      meta={note ? <span className="tasklist__note">{note}</span> : null}
      trailing={
        <>
          {repeats && !item.done_at && (
            <span className="tasklist__repeat" role="img" aria-label={`Repeats ${repeats}`}>
              <RepeatIcon size={16} aria-hidden="true" />
            </span>
          )}
          {due && (
            <button type="button" className="tasklist__duebtn" onClick={onOpenSheet} aria-label={`Due ${due.text}, change`}>
              <Tag tone={due.tone}>{due.text}</Tag>
            </button>
          )}
          {more(!item.done_at)}
        </>
      }
      swipe={swipe}
    />
  )
}

// ---------------------------------------------------------------------------
// L8: an event, inside a list
// ---------------------------------------------------------------------------

/** The thumbnail, copied from `EventRow.tsx`'s own `Leader` rather than
 *  imported — that component is not in this package's *Files*, and its
 *  ref/view-transition plumbing is for the feed's shared-element open, which
 *  a list item's simpler tap does not do. `Shortlist.tsx`'s own `Thumb` is the
 *  same copy for the same reason. */
function Thumb({ e }: { e: FeedRow }) {
  const now = new Date()
  const kind = leadingOf(e, { url: heroUrl(e, 112), isVenue: heroIsVenue(e), poster: posterUrl(e, 'w185'), now })
  const plate = kind.kind === 'poster' ? null : plateFor(e, now)
  return (
    <span className={`ev-lead ev-lead--${kind.kind}`}
      style={plate ? ({ '--ev-hue': plate.hue } as { [k: string]: string }) : undefined}>
      {plate && (
        <span className="ev-plate">
          <b>{plate.big}</b>
          {plate.small && <i>{plate.small}</i>}
        </span>
      )}
      {kind.kind !== 'plate' && (
        <img src={kind.url} alt="" width={56} height={56} loading="lazy" decoding="async" />
      )}
    </span>
  )
}

/** L8: a list item that carries a live `entry_id` renders with the event
 *  row's own anatomy — `EventRow.tsx`'s leading picture, `rowTitle` and
 *  `metaLine` — rather than the list's usual text row. `trailing` is the
 *  one thing that stays the list's own (a checkbox here, a flag or a status
 *  control in the other three kinds); tapping opens the event's sheet, never
 *  the item editor. Rule 2: nothing about the event is duplicated beyond the
 *  `body` this row never reads. */
function EventItemRow({ entry, trailing, swipe, leaving, onOpen }: {
  entry: FeedRow
  trailing: ReactNode
  swipe?: { left?: SwipeAction; right?: SwipeAction }
  leaving?: boolean
  onOpen: () => void
}) {
  const now = new Date()
  const m = metaLine(entry, { now })
  const rest = [m.venue, m.fact].filter(Boolean).join(' · ')
  return (
    <Row
      leaving={leaving}
      leading={<Thumb e={entry} />}
      title={rowTitle(entry)}
      meta={
        <>
          <span className="ev-row__dot" style={{ background: m.dot }} aria-hidden="true" />
          <b className="ev-row__when">{m.when}</b>
          {rest && <span className="ev-row__rest">{rest}</span>}
        </>
      }
      trailing={trailing}
      onPress={onOpen}
      swipe={swipe}
    />
  )
}
